import { ReactNode, RefObject } from 'react';
import { Loggable } from '@direct-frontend/modules-logger';

import { TestProps } from '../../internal/types/testTypes';
import { OverlayBackDismissProps, OverlayProps } from '../Overlay/Overlay';

export type DrawerProps = TestProps &
    OverlayBackDismissProps &
    Pick<OverlayProps, 'priority'> &
    Loggable & {
        /**
         * Компонент для добавления статичной шапки с заголовком и кнопками (можно использовать в случаях,
         * когда нужно прокручивать контент, но шапка при этом должна оставаться статичной)
         */
        header?: ReactNode;
        /**
         * Компонент для добавления статичной подвала
         */
        footer?: ReactNode;

        /**
         * Дочерние элементы
         */
        children?: ReactNode;

        /**
         * Направление, откуда появляется шторка.
         * @default 'bottom'
         */
        direction?: 'bottom' | 'left' | 'right';

        /**
         * Переключение между открытым и закрытым состояниями
         * @default false
         */
        isOpen?: boolean;

        /**
         * Действие, выполняемое при закрытии
         */
        onClose?: () => void;

        /**
         * Требуется ли закрывать компонент при клике на затемнённый фон (Overlay)
         * @default true
         */
        dismissOnOutsideClick?: boolean;

        /**
         * Признак возможности перемещения шторки (при выключении шторку нельзя двигать мышью или пальцем)
         * @default true
         */
        isDraggable?: boolean;

        /**
         * Максимальный размер шторки (ширина или высота в зависимости от `direction`).
         * Принимает любое валидное CSS значение.
         * @default 100%
         */
        maxSize?: string;

        /**
         * Названия классов для элемента
         */
        className?: string;

        /**
         * Открывать шторку на весь вьюпорт, даже когда контента мало
         */
        isFullscreen?: boolean;
        /**
         * Определяет стиль отображения:
         * - drawer - стандартное поведение с отрисовкой шторки с паддингами и др элементами.
         * - overlay - сохраняет поведение drawer, но убирает бекграунд, все паддинги и элемент drag handler.
         *
         * @default 'drawer'
         */
        mode?: 'drawer' | 'overlay';
        /**
         * Цвет шторки
         * @default 'gray'
         */
        color?: 'gray' | 'white';

        /**
         * Состояние загрузки, поверх контента показывается паранжа
         */
        isLoading?: boolean;

        /**
         * Коллбек, вызываемый при завершении анимации
         * @param animationState
         */
        onAnimationEnd?: (animationState: ANIMATION_STATES) => void;

        /**
         * Ссылка на блок с контентом (блок, который может быть прокручиваемым).
         * Нужно для того, чтобы передать кастомную ссылку на блок с прокруткой
         * Если этот блок прокручен до верху и идет свайп вниз - drawer закроется
         */
        contentRef?: RefObject<HTMLDivElement>;

        /**
         * Названия классов для контейнера Overlay
         */
        overlayClassName?: string;

        /**
         * Названия классов для контейнера Drawer
         */
        containerClassName?: string;
    };

/**
 * Состояния для переключения анимации
 */
export enum ANIMATION_STATES {
    NONE = '',
    OPEN = 'open',
    CLOSE = 'close',
}

/**
 * Промежуточное состояние компонента между событиями onTouchStart/onMouseDown и onTouchEnd/onMouseUp
 */
export interface DragStateData {
    isTargetUnderContent?: boolean;
    initialScrollPosition?: number;
    isScrolled: boolean;
}

export interface DrawerTransformParams {
    /**
     * Ось анимирования шторки
     */
    axis: 'x' | 'y';

    /**
     * Прогресс открытия шторки (1 - открыта, 0 - закрыта)
     */
    openProgress: number;

    /**
     * Коэффициент направления оси (-1 - лево, 1 - право)
     */
    directionRatio: 1 | -1;
    /**
     * Количество пикселей, на которые пользователь "оттянул" шторку из изначального положения
     */
    movement?: number;
}

export type DrawerRefs = {
    /**
     * Ссылка на блок с контейнером (блок, который может менять свое положение)
     */
    containerRef: RefObject<HTMLDivElement>;

    /**
     * Ссылка на блок с контентом (блок, который может быть прокручиваемым)
     */
    contentRef: RefObject<HTMLDivElement>;
};

export type DrawerDragParams = DrawerRefs &
    Omit<DrawerProps, 'data-testid' | 'header' | 'children' | 'dismissOnOutsideClick' | 'className'>;
